// src/components/ui/Button.jsx - Ensuring proper burgundy styling
import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Button component with consistent styling across the application
 * 
 * @param {Object} props - The component props
 * @param {string} [props.variant='primary'] - Button variant (primary, secondary, outline, accent, text)
 * @param {string} [props.size='md'] - Button size (sm, md, lg)
 * @param {string} [props.href] - If provided, renders as a Link component
 * @param {string} [props.type='button'] - HTML button type
 * @param {boolean} [props.fullWidth=false] - Whether the button should take full width
 * @param {React.ReactNode} props.children - Button content
 * @param {Function} [props.onClick] - Click handler
 * @param {string} [props.className] - Additional CSS classes
 */
const Button = ({
  variant = 'primary',
  size = 'md',
  href,
  type = 'button',
  fullWidth = false,
  children,
  onClick,
  className = '',
  ...props
}) => {
  // Base styles
  const baseStyles = "inline-flex items-center justify-center rounded font-medium transition duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2";
  
  // Size styles
  const sizeStyles = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-5 py-2.5 text-base",
    lg: "px-6 py-3 text-lg",
  };
  
  // Variant styles - Updated for better burgundy secondary styling
  const variantStyles = {
    primary: "bg-primary hover:bg-primary-dark text-white focus:ring-primary-light",
    secondary: "bg-secondary hover:bg-secondary-dark text-white focus:ring-secondary-light",
    outline: "border-2 border-primary text-primary hover:bg-primary-50 focus:ring-primary-light",
    outlineSecondary: "border-2 border-secondary text-secondary hover:bg-secondary-50 focus:ring-secondary-light", // Added outline secondary style
    accent: "bg-accent hover:bg-accent-dark text-white focus:ring-accent-light",
    text: "text-primary hover:text-primary-dark underline-offset-2 hover:underline focus:ring-primary-light bg-transparent",
    textSecondary: "text-secondary hover:text-secondary-dark underline-offset-2 hover:underline focus:ring-secondary-light bg-transparent", // Added text secondary style
  };
  
  // Combined styles
  const combinedStyles = `
    ${baseStyles}
    ${sizeStyles[size] || sizeStyles.md}
    ${variantStyles[variant] || variantStyles.primary}
    ${fullWidth ? 'w-full' : ''}
    ${className}
  `.trim();
  
  // If href is provided, render as Link
  if (href) {
    return (
      <Link to={href} className={combinedStyles} {...props}>
        {children}
      </Link>
    );
  }
  
  // Otherwise render as button
  return (
    <button 
      type={type} 
      className={combinedStyles} 
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

export default Button;